/**
 * This month's projected close, from actuals, against the plan for the month.
 * See lib/scienceModelProjection for the method.
 */

import { useState, useEffect, useMemo } from 'react'
import { Loader2 } from 'lucide-react'

import { scienceModelAPI, manifoldAPI } from '../../services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import { projectMonth } from '../../lib/scienceModelProjection'
import { formatMoney, formatVariance, monthLabel, variance } from '../../lib/scienceModelView'

const count = v => (v == null ? '—' : Math.round(v).toLocaleString())

function Figure({ title, line, plan, format = formatMoney, invert = false, note }) {
  const delta = line?.projected != null && plan ? variance(line.projected, plan) : null
  const good = delta == null ? null : invert ? delta <= 0 : delta >= 0
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-muted-foreground">{title}</div>
      <div className="text-xl font-semibold tabular-nums">{line?.projected == null ? '—' : format(line.projected)}</div>
      <div className="text-xs text-muted-foreground tabular-nums">
        {format(line?.toDate)} so far
        {plan != null && <> · plan {format(plan)}</>}
        {delta != null && (
          <span className={`ml-1.5 font-medium ${good ? 'text-foreground' : 'text-destructive'}`}>{good ? '▲' : '▼'} {formatVariance(delta)}</span>
        )}
      </div>
      {note && <div className="text-[11px] text-muted-foreground">{note}</div>}
    </div>
  )
}

/**
 * @param {object[]} recent     last full days (Daily Report rows) for the run-rate
 * @param {object|null} planRow the model's row for this month
 */
export default function MonthProjection({ recent, planRow }) {
  const month = useMemo(() => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date()).slice(0, 7), [])
  const [actual, setActual] = useState(null)
  const [scheduled, setScheduled] = useState(undefined)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    scienceModelAPI.getDailyActuals({ month })
      .then(r => { if (alive && r.data.success) setActual(r.data.data) })
      .catch(err => { if (alive) setError(err.response?.data?.error?.message || err.message) })
    // Renewals still due this month (cents): today onward on the calendar.
    manifoldAPI.getRenewalCalendar({ month })
      .then(r => {
        if (!alive || !r.data.success) return
        const cal = r.data.data
        setScheduled(cal.days.filter(d => d.date >= cal.today).reduce((s, d) => s + d.scheduledAmount, 0) / 100)
      })
      .catch(() => { if (alive) setScheduled(null) })
    return () => { alive = false }
  }, [month])

  const p = useMemo(
    () => (actual && recent && scheduled !== undefined ? projectMonth(actual, recent, scheduled) : null),
    [actual, recent, scheduled]
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{monthLabel(month)} projection</CardTitle>
        <CardDescription className="text-xs">
          {p
            ? <>Month to date plus the last {p.runRateDays} full days&apos; average for the {p.daysLeft.toFixed(1)} days left; renewals from the subscription schedule.</>
            : 'Month to date plus the recent daily average for the days left; renewals from the subscription schedule.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <p className="text-xs text-destructive">Could not load this month: {error}</p>}
        {!p && !error && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Projecting…</div>}
        {p && (
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Figure title="Revenue" line={p.revenue} plan={planRow?.revenue} />
            <Figure title="New-customer revenue" line={p.newRevenue} plan={planRow?.newRevenue}
              note={p.newRevenue.perDay != null ? `${formatMoney(p.newRevenue.perDay)}/day` : null} />
            <Figure title="Renewals" line={p.renewals} plan={planRow?.recurringRevenue}
              note={p.renewals.scheduled == null ? 'Schedule unavailable' : `${formatMoney(p.renewals.scheduled)} still scheduled`} />
            <Figure title="New customers" line={p.customers} plan={planRow?.newCustomers} format={count}
              note={p.customers.perDay != null ? `${p.customers.perDay.toFixed(1)}/day` : null} />
            <Figure title="Ad spend" line={p.spend} plan={planRow?.spend} invert
              note={p.spend.perDay != null ? `${formatMoney(p.spend.perDay)}/day` : 'No spend reported'} />
            <Figure title="CAC" line={p.cac} plan={planRow?.cac} invert />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
